import { useState, useEffect, useRef, useCallback } from "react";
import Btn from "./Btn";
import Img, { webpSrcSet } from "./Img";
import Countdown from "./Countdown";
import { TRIPS_LIVE, tripsLive } from "../data/launch";

// Tall (portrait phone) screens get a 9:16 center crop of the clip and its first frame:
// the landscape files are cropped to that region by object-cover anyway, so this is the
// same picture at a fraction of the bytes (video 2.1MB vs 7.9MB, poster ~20KB).
export const PORTRAIT = "(max-aspect-ratio: 3/4)";
export const LOCKUP_SIZES = "(min-width: 768px) 290px, 215px";

/** Slow or metered connections keep the poster; the loop is decoration. */
const skipVideo = () => {
  const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } })
    .connection;
  return !!c && (c.saveData === true || /^(slow-2g|2g|3g)$/.test(c.effectiveType ?? ""));
};

/** Full-screen video hero — self-hosted (July 2026).
 *  Replaces the old YouTube IFrame API hack entirely: no third-party script,
 *  no cookies, no caption-killing workarounds. 30s loop (max conservative under Cloudflare 25MB asset cap), 1080p from the 4K source (CRF 24), no audio,
 *  starts 1s into the source so the loop never flashes the black fade-in.
 *  The poster is the video's exact first frame, so playback starts seamlessly. */
const HeroSection = () => {
  const [loadVideo, setLoadVideo] = useState(false);
  const [variant, setVariant] = useState<"portrait" | "720" | "1080">("1080");
  const [paused, setPaused] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  // Prerender (build time) is always before launch; the client flips on mount if past it.
  const [live, setLive] = useState(false);
  const goLive = useCallback(() => setLive(true), []);
  useEffect(() => setLive(tripsLive()), []);

  // The poster is the LCP; keep the video off the network until the page has loaded
  // and the main thread is idle, so it never competes with first paint.
  // prefers-reduced-motion: skip the video entirely — poster only (WCAG 2.2.2 / 2.3.3).
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || skipVideo()) return;
    setVariant(
      window.matchMedia(PORTRAIT).matches
        ? "portrait"
        : window.matchMedia("(max-width: 768px)").matches
          ? "720"
          : "1080"
    );
    let idle = 0;
    const start = () => {
      const ric = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 200));
      idle = ric(() => setLoadVideo(true));
    };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => {
      window.removeEventListener("load", start);
      (window.cancelIdleCallback ?? window.clearTimeout)(idle);
    };
  }, []);

  const togglePlayback = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPaused(false);
    } else {
      v.pause();
      setPaused(true);
    }
  };

  return (
    <section
      className="relative flex h-screen w-full items-center justify-center overflow-hidden"
      style={{ height: "100svh" }} /* small-viewport height: no jump when mobile URL bar hides */
    >
      {/* Poster image — instant paint, identical to the video's first frame */}
      <picture>
        <source media={PORTRAIT} srcSet="/img/hero-poster-portrait-480.webp" type="image/webp" />
        <source srcSet={webpSrcSet("/images/hero-poster.jpg")} sizes="100vw" type="image/webp" />
        <img
          src="/images/hero-poster.jpg"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          loading="eager"
          {...{ fetchpriority: "high" }}
        />
      </picture>

      {loadVideo && (
        <video
          ref={videoRef}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          tabIndex={-1}
        >
          {variant === "portrait" ? (
            <source src="/videos/hero-portrait.mp4" type="video/mp4" />
          ) : (
            <>
              <source src={`/videos/hero-${variant}.webm`} type="video/webm" />
              <source src={`/videos/hero-${variant}.mp4`} type="video/mp4" />
            </>
          )}
        </video>
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/55" />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-3xl px-5 text-center">
        {/* Official primary lockup — exact brand-kit artwork, reversed white */}
        <h1 className="sr-only">HXP — Humanitarian Experience: Youth Humanitarian Trips for Teens 14–19</h1>
        <Img
          sizes={LOCKUP_SIZES}
          src="/images/hxp-lockup-white.png"
          alt="HXP — Humanitarian Experience"
          className="mx-auto h-44 w-auto md:h-60"
          width={960}
          height={791}
          loading="eager"
          {...{ fetchpriority: "high" }} /* the lockup is the LCP element on phones */
          decoding="async"
        />
        <p className="accent-hand mx-auto mt-7 max-w-3xl text-[1.7rem] leading-snug text-white md:text-5xl">
          the hardest and best summer
          <br className="md:hidden" /> of your life.
        </p>
        {live ? (
          <div className="mt-8">
            <Btn href="/go/register-home-hero">Register</Btn>
          </div>
        ) : (
          <>
            <p className="font-heading mb-4 mt-8 text-lg uppercase leading-none text-white/90 md:text-2xl">
              2027 Trips go live in:
            </p>
            <Countdown target={TRIPS_LIVE} onDone={goLive} />
            <div className="mt-8">
              <Btn href="/registration/">Registration</Btn>
            </div>
          </>
        )}
      </div>

      {/* Pause control for the background video (WCAG 2.2.2) */}
      {loadVideo && (
        <button
          type="button"
          onClick={togglePlayback}
          aria-label={paused ? "Play background video" : "Pause background video"}
          className="btn-caps absolute bottom-5 right-5 z-10 rounded-sm border border-white/40 px-3 py-1.5 text-xs text-white/90 transition-colors hover:border-white/70 hover:text-white"
        >
          {paused ? "Play" : "Pause"}
        </button>
      )}

    </section>
  );
};

export default HeroSection;
